import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import AccountPickerContext from '../../contexts/AccountPickerContext';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import LocalTimelineContext from '../../contexts/Timeline/LocalTimelineContext';
import getIdentifier from '../../getIdentifier';
import getTimeStamp from '../../getTimeStamp';
import addTimelineEvent from './addTimelineEvent';
import getTimelineEventsSnapshot from './getTimelineEventsSnapshot';

const LocalTimelineProvider = ({
  children,
  sources,
  destination,
  startsOn,
}: {
  children: ReactNode;
  sources: Timeline.EventSource[];
  destination?: Timeline.EventSource;
  startsOn?: string;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const { isWallboardUser } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [events, setEvents] = useState<Timeline.Event[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isWallboardUser) {
      setIsLoading(false);
      return;
    }

    const sourceIds = sources.map((s) => s.id);
    if (sourceIds.length === 0) {
      setIsLoading(false);
      return;
    }

    let isActive = true;

    let query = getTimelineEventsSnapshot({
      accountId: selectedAccountId,
    }).where('timelineableId', 'in', sourceIds);

    if (startsOn) {
      query = query.where('createdOn', '>', startsOn);
    }

    query = query.orderBy('createdOn', 'desc');

    const unsubscribe = query.onSnapshot(
      (snapshot: firebase.default.firestore.QuerySnapshot) => {
        if (!isActive) {
          return;
        }
        const data: Timeline.Event[] = [];
        snapshot.docs.forEach((d) =>
          data.push({ id: d.id, ...d.data() } as Timeline.Event),
        );

        const sourceTypes = new Set(sources.map((s) => s.type));
        const filtered = data.filter((event) =>
          sourceTypes.has(event.timelineableType),
        );

        setEvents(filtered);
        setIsLoading(false);
      },
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [isWallboardUser, selectedAccountId, sources, startsOn]);

  const addEvent = useCallback(
    async ({
      actionText,
      contentText,
      contextText,
      interaction,
      isContentTextRich,
      destinationOverride,
    }: {
      actionText: string;
      contextText: string;
      contentText?: string;
      interaction?: Timeline.Interaction;
      isContentTextRich?: boolean;
      destinationOverride?: Timeline.EventSource;
    }) => {
      const destinationToUse = destinationOverride
        ? destinationOverride
        : destination;
      if (!destinationToUse) {
        return;
      }
      const timelineableId = destinationToUse.id;
      const timelineableType = destinationToUse.type;

      if (Array.isArray(timelineableId)) {
        const error = new Error();
        error.name = 'Cannot add events in this context';
        throw error;
      }

      const event = {
        id: getIdentifier(),
        timelineableId,
        timelineableType,
        createdOn: getTimeStamp(),
        createdBy: currentUser.id,
        userId: currentUser.id,
        headlineText: {
          userDisplayName: currentUser.displayName,
          actionText,
          contextText,
        },
        contentText,
        interaction,
        isContentTextRich,
      };

      await addTimelineEvent(event, selectedAccountId);
    },
    [selectedAccountId, currentUser.displayName, currentUser.id, destination],
  );

  return (
    <LocalTimelineContext.Provider
      value={{
        events,
        isLoading,
        addEvent,
      }}
    >
      {children}
    </LocalTimelineContext.Provider>
  );
};

export default LocalTimelineProvider;
